import { ClinicSwitcher } from '@/components/ClinicSwitcher';
import { HelpAndFeedback } from '@/components/FeedbackButton';
import { LOGOUT_LABEL, LogoutButton, UserInitials } from '@/components/UserCard';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import type { User } from '@/contexts/AuthContext';
import {
  hasClinicAdminAccess,
  hasMemoryAccess,
  hasSystemAdminAccess,
  hasVerkefniAccess,
  useAuth,
} from '@/contexts/AuthContext';
import { useTelehealth } from '@/hooks/useTelehealth';
import { cn } from '@/lib/utils';
import { formatDelegatorLabel } from '@/types/delegate';
import {
  Activity,
  AlertTriangle,
  BarChart3,
  Brain,
  Building2,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Eye,
  FlaskConical,
  HelpCircle,
  LayoutDashboard,
  LayoutTemplate,
  Menu,
  Mic,
  ReceiptText,
  Settings,
  ShieldCheck,
  X,
} from 'lucide-react';
import Video from 'lucide-react/dist/esm/icons/video';
import { useState } from 'react';
import { Link, useLocation } from 'wouter';

// Shared navigation item definitions (Verkefnalisti is conditionally added based on clinic settings)
const BASE_NAV_ITEMS = [
  { icon: LayoutDashboard, label: 'Mælaborð', href: '/' },
  { icon: Mic, label: 'Ný lota', href: '/session/new', hasNewSessionHandler: true },
];

const CLINIC_ADMIN_ITEMS = [{ icon: Building2, label: 'Stöðvarstillingar', href: '/clinic-settings' }];

const SYSTEM_ADMIN_ITEMS = [
  { icon: ShieldCheck, label: 'Kerfisumsjón', href: '/admin' },
  { icon: ReceiptText, label: 'Notkun og kostnaður', href: '/admin/usage-costs' },
  { icon: BarChart3, label: 'Gæðagreining', href: '/summary-quality' },
];

const MODEL_REVIEWER_ITEMS = [{ icon: FlaskConical, label: 'Módelprófun', href: '/model-review' }];

const QA_ENGINEER_ITEMS = [{ icon: Activity, label: 'Prófunarsetur', href: '/test-suite' }];

// Build the main navigation items array, shared between Sidebar and Header
const buildNavItems = (user: User | null, handleNewSession: () => void, showVideo = false) => {
  const showVerkefni = hasVerkefniAccess(user);

  return [
    ...BASE_NAV_ITEMS,
    ...(showVideo ? [{ icon: Video, label: 'Myndsímtöl', href: '/video' }] : []),
    ...(showVerkefni ? [{ icon: ClipboardList, label: 'Verkefnalisti', href: '/tasks' }] : []),
    { icon: LayoutTemplate, label: 'Sniðmát', href: '/templates' },
    ...(hasMemoryAccess(user) ? [{ icon: Brain, label: 'Minni', href: '/settings/memory' }] : []),
    { icon: HelpCircle, label: 'Hjálparmiðstöð', href: '/faqs' },
    { icon: Settings, label: 'Stillingar', href: '/settings' },
  ].map((item) => ({
    ...item,
    hasNewSessionHandler: 'hasNewSessionHandler' in item ? item.hasNewSessionHandler : false,
    onClick: 'hasNewSessionHandler' in item && item.hasNewSessionHandler ? handleNewSession : undefined,
  }));
};

const isNavItemActive = (location: string, href: string) => {
  if (href === '/faqs') {
    return location === '/faq' || location.startsWith('/faq/') || location === '/faqs' || location.startsWith('/faqs/');
  }

  if (href === '/templates') {
    return location === '/templates' || location.startsWith('/templates/');
  }

  return location === href;
};

// Map the user's clinical discipline (label) to Icelandic display text. Disciplines
// come from auth as labels (transcriber_db clinical_roles.py naming); the admin
// fallbacks are derived from permissions, not from any legacy role list.
const getUserDisplayTitle = (user?: User | null): string => {
  const clinicalDisciplines = new Set(['DOCTOR', 'PSYCHOLOGIST', 'NURSE', 'SOCIAL_WORKER', 'PHYSIOTHERAPIST']);

  const disciplineMap: Record<string, string> = {
    // Clinical disciplines
    DOCTOR: 'Læknir',
    NURSE: 'Hjúkrunarfræðingur',
    PSYCHOLOGIST: 'Sálfræðingur',
    PHYSIOTHERAPIST: 'Sjúkraþjálfari',
    SOCIAL_WORKER: 'Félagsráðgjafi',
    // Permission-derived administrative titles
    SYSTEM_ADMIN: 'Kerfisstjóri',
    CLINIC_ADMIN: 'Stjórnandi',
  };

  const disciplines = user?.disciplines;

  if (disciplines && disciplines.length > 0) {
    const clinicalDiscipline = disciplines.find((discipline) => clinicalDisciplines.has(discipline));
    if (clinicalDiscipline) {
      return disciplineMap[clinicalDiscipline] || clinicalDiscipline;
    }
    // Non-clinical discipline label: surface the first one as-is.
    return disciplineMap[disciplines[0]] || disciplines[0];
  }

  if (hasSystemAdminAccess(user ?? null)) {
    return disciplineMap.SYSTEM_ADMIN;
  }
  if (hasClinicAdminAccess(user ?? null)) {
    return disciplineMap.CLINIC_ADMIN;
  }

  return 'Starfsmaður';
};

export function Sidebar() {
  const [location] = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const { user, authOrgName, logout, isImpersonating, stopImpersonation } = useAuth();
  const [isStoppingImpersonation, setIsStoppingImpersonation] = useState(false);

  const handleStopImpersonation = async () => {
    setIsStoppingImpersonation(true);
    const success = await stopImpersonation();
    if (success) {
      // Redirect to admin page after stopping
      window.location.href = '/admin';
    }
    setIsStoppingImpersonation(false);
  };

  const handleNewSession = () => {
    // Clear patient selection when starting a new session
    sessionStorage.removeItem('currentSelectedPatient');
  };

  const video = useTelehealth();
  const navItems = buildNavItems(user, handleNewSession, video.data?.enabled === true);
  const currentOrgName = authOrgName;
  // Annotation/QA review tooling (MODEL_REVIEWER/QA_ENGINEER) stays on legacy
  // transcriber_db roles that auth-mode does not carry; gate it behind system
  // admin in scribe so it stays reachable.
  const showModelReviewerItems = hasSystemAdminAccess(user);
  const showQaEngineerItems = hasSystemAdminAccess(user);
  const showClinicAdminItems = hasClinicAdminAccess(user);
  const showSystemAdminItems = hasSystemAdminAccess(user);

  return (
    <div
      className={cn(
        'relative flex hidden h-screen flex-col border-sidebar-border border-r bg-sidebar transition-all duration-300 md:flex',
        isCollapsed ? 'w-20' : 'w-64'
      )}
    >
      <Button
        variant="ghost"
        size="icon"
        className="-right-3 absolute top-20 z-30 h-6 w-6 rounded-full border border-border bg-background shadow-sm hover:bg-accent"
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        {isCollapsed ? <ChevronRight className="h-3 w-3" /> : <ChevronLeft className="h-3 w-3" />}
      </Button>

      <div className={cn('flex items-center justify-center border-sidebar-border border-b p-6', isCollapsed && 'px-4')}>
        {!isCollapsed ? (
          <img src="/careflux.png" alt="Careflux" className="h-24 w-auto object-contain" />
        ) : (
          <img src="/careflux.png" alt="Careflux" className="h-16 w-16 object-contain" />
        )}
      </div>

      {/* Impersonation Banner */}
      {isImpersonating && (
        <div className={cn('mx-3 my-3 rounded-lg border border-amber-200 bg-amber-50 p-3', isCollapsed && 'mx-2 p-2')}>
          {!isCollapsed ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-amber-700">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                <span className="font-semibold text-xs uppercase tracking-wider">Kerfisstjórahamur</span>
              </div>
              <p className="text-amber-600 text-xs">
                Innskráð/ur sem: <span className="font-medium">{user?.name}</span>
              </p>
              <Button
                size="sm"
                variant="outline"
                className="h-7 w-full border-amber-300 text-amber-700 text-xs hover:bg-amber-100"
                onClick={handleStopImpersonation}
                disabled={isStoppingImpersonation}
              >
                <X className="mr-1 h-3 w-3" />
                Hætta
              </Button>
            </div>
          ) : (
            <button
              type="button"
              onClick={handleStopImpersonation}
              disabled={isStoppingImpersonation}
              className="flex w-full items-center justify-center text-amber-700 hover:text-amber-900"
              title="Hætta sem notanda"
            >
              <AlertTriangle className="h-5 w-5" />
            </button>
          )}
        </div>
      )}

      <div data-testid="sidebar-navigation-scroll" className="min-h-0 flex-1 overflow-y-auto">
        <nav className="space-y-1 p-4">
          {navItems.map((item) => (
            <Link key={item.href} href={item.href} onClick={item.onClick}>
              <div
                title={isCollapsed ? item.label : ''}
                className={cn(
                  'flex cursor-pointer items-center gap-3 rounded-md px-3 py-2.5 font-medium text-sm transition-all duration-200',
                  isNavItemActive(location, item.href)
                    ? 'bg-sidebar-primary/10 text-sidebar-primary'
                    : 'text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-foreground',
                  isCollapsed && 'justify-center px-0'
                )}
              >
                <item.icon className="h-4 w-4 shrink-0" />
                {!isCollapsed && <span>{item.label}</span>}
              </div>
            </Link>
          ))}
        </nav>

        {showModelReviewerItems && (
          <div className="px-4 py-2">
            <div className="mb-2 h-px w-full bg-sidebar-border" />
            <nav className="space-y-1">
              {MODEL_REVIEWER_ITEMS.map((item) => (
                <Link key={item.href} href={item.href}>
                  <div
                    title={isCollapsed ? item.label : ''}
                    className={cn(
                      'flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 font-medium text-xs transition-all duration-200',
                      isNavItemActive(location, item.href)
                        ? 'bg-primary/10 text-primary'
                        : 'text-sidebar-foreground/60 hover:bg-sidebar-accent hover:text-sidebar-foreground',
                      isCollapsed && 'justify-center px-0'
                    )}
                  >
                    <item.icon className="h-3.5 w-3.5 shrink-0" />
                    {!isCollapsed && <span>{item.label}</span>}
                  </div>
                </Link>
              ))}
            </nav>
          </div>
        )}

        {showQaEngineerItems && (
          <div className="px-4 py-2">
            <div className="mb-2 h-px w-full bg-sidebar-border" />
            <nav className="space-y-1">
              {QA_ENGINEER_ITEMS.map((item) => (
                <Link key={item.href} href={item.href}>
                  <div
                    title={isCollapsed ? item.label : ''}
                    className={cn(
                      'flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 font-medium text-xs transition-all duration-200',
                      isNavItemActive(location, item.href)
                        ? 'bg-primary/10 text-primary'
                        : 'text-sidebar-foreground/60 hover:bg-sidebar-accent hover:text-sidebar-foreground',
                      isCollapsed && 'justify-center px-0'
                    )}
                  >
                    <item.icon className="h-3.5 w-3.5 shrink-0" />
                    {!isCollapsed && <span>{item.label}</span>}
                  </div>
                </Link>
              ))}
            </nav>
          </div>
        )}

        {showClinicAdminItems && (
          <div className="px-4 py-2">
            <div className="mb-2 h-px w-full bg-sidebar-border" />
            <nav className="space-y-1">
              {CLINIC_ADMIN_ITEMS.map((item) => (
                <Link key={item.href} href={item.href}>
                  <div
                    title={isCollapsed ? item.label : ''}
                    className={cn(
                      'flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 font-medium text-xs transition-all duration-200',
                      isNavItemActive(location, item.href)
                        ? 'bg-primary/10 text-primary'
                        : 'text-sidebar-foreground/60 hover:bg-sidebar-accent hover:text-sidebar-foreground',
                      isCollapsed && 'justify-center px-0'
                    )}
                  >
                    <item.icon className="h-3.5 w-3.5 shrink-0" />
                    {!isCollapsed && <span>{item.label}</span>}
                  </div>
                </Link>
              ))}
              {showSystemAdminItems &&
                SYSTEM_ADMIN_ITEMS.map((item) => (
                  <Link key={item.href} href={item.href}>
                    <div
                      title={isCollapsed ? item.label : ''}
                      className={cn(
                        'flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 font-medium text-xs transition-all duration-200',
                        isNavItemActive(location, item.href)
                          ? 'bg-primary/10 text-primary'
                          : 'text-sidebar-foreground/60 hover:bg-sidebar-accent hover:text-sidebar-foreground',
                        isCollapsed && 'justify-center px-0'
                      )}
                    >
                      <item.icon className="h-3.5 w-3.5 shrink-0" />
                      {!isCollapsed && <span>{item.label}</span>}
                    </div>
                  </Link>
                ))}
            </nav>
          </div>
        )}
      </div>

      <div className={cn('px-4 pb-2', isCollapsed && 'px-2')}>
        <HelpAndFeedback collapsed={isCollapsed} />
      </div>

      <div className={cn('border-sidebar-border border-t p-4', isCollapsed && 'px-2')}>
        {isCollapsed ? (
          <button
            type="button"
            onClick={logout}
            aria-label={LOGOUT_LABEL}
            title={LOGOUT_LABEL}
            className="flex w-full cursor-pointer items-center justify-center rounded-md bg-sidebar-accent/50 p-2 transition-colors hover:bg-sidebar-accent"
          >
            <UserInitials user={user} />
          </button>
        ) : (
          <div className="flex w-full items-center gap-3 rounded-md bg-sidebar-accent/50 p-3">
            <UserInitials user={user} />
            <div className="flex-1 overflow-hidden">
              <p className="truncate font-medium text-sm">{user?.name || 'Notandi'}</p>
              <p className="truncate text-muted-foreground text-xs">{getUserDisplayTitle(user)}</p>
              {currentOrgName && (
                <p className="mt-0.5 truncate text-[10px] text-muted-foreground/70" title={currentOrgName}>
                  {currentOrgName}
                </p>
              )}
            </div>
            <ClinicSwitcher />
            <LogoutButton onLogout={logout} />
          </div>
        )}
      </div>
    </div>
  );
}

export function Header({
  title,
  children,
  centerContent,
}: { title?: string; children?: React.ReactNode; centerContent?: React.ReactNode }) {
  const [location] = useLocation();
  const { user, authOrgName, logout, isViewingAsDelegate, viewingAsAccount, isImpersonating, stopImpersonation } =
    useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isStoppingImpersonation, setIsStoppingImpersonation] = useState(false);

  const handleStopImpersonation = async () => {
    setIsStoppingImpersonation(true);
    const success = await stopImpersonation();
    if (success) {
      window.location.href = '/admin';
    }
    setIsStoppingImpersonation(false);
  };

  const handleNewSession = () => {
    sessionStorage.removeItem('currentSelectedPatient');
    setMobileMenuOpen(false);
  };

  const video = useTelehealth();
  const navItems = buildNavItems(user, handleNewSession, video.data?.enabled === true);
  const currentOrgName = authOrgName;
  // Annotation/QA review tooling (MODEL_REVIEWER/QA_ENGINEER) stays on legacy
  // transcriber_db roles that auth-mode does not carry; gate it behind system
  // admin in scribe so it stays reachable.
  const showModelReviewerItems = hasSystemAdminAccess(user);
  const showQaEngineerItems = hasSystemAdminAccess(user);
  const showClinicAdminItems = hasClinicAdminAccess(user);
  const showSystemAdminItems = hasSystemAdminAccess(user);

  return (
    <header className="relative sticky top-0 z-10 flex h-14 items-center justify-between border-border border-b bg-white px-3 md:h-16 md:px-6">
      <div className="flex items-center gap-3">
        {/* Mobile hamburger menu */}
        <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="sm" className="h-10 w-10 p-0 md:hidden">
              <Menu className="h-6 w-6" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-72 p-0">
            <div className="flex h-full flex-col">
              {/* Logo */}
              <div className="flex items-center justify-center border-b p-4">
                <img src="/careflux.png" alt="Careflux" className="h-16 w-auto object-contain" />
              </div>

              {/* Impersonation Banner */}
              {isImpersonating && (
                <div className="mx-3 my-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-amber-700">
                      <AlertTriangle className="h-4 w-4 shrink-0" />
                      <span className="font-semibold text-xs uppercase tracking-wider">Kerfisstjórahamur</span>
                    </div>
                    <p className="text-amber-600 text-xs">
                      Innskráð/ur sem: <span className="font-medium">{user?.name}</span>
                    </p>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 w-full border-amber-300 text-amber-700 text-xs hover:bg-amber-100"
                      onClick={handleStopImpersonation}
                      disabled={isStoppingImpersonation}
                    >
                      <X className="mr-1 h-3 w-3" />
                      Hætta
                    </Button>
                  </div>
                </div>
              )}

              {/* Navigation */}
              <nav className="min-h-0 flex-1 space-y-1 overflow-y-auto p-4">
                {navItems.map((item) => (
                  <Link
                    key={item.href}
                    href={item.href}
                    onClick={() => {
                      item.onClick?.();
                      setMobileMenuOpen(false);
                    }}
                  >
                    <div
                      className={cn(
                        'flex cursor-pointer items-center gap-3 rounded-md px-3 py-2.5 font-medium text-sm transition-all duration-200',
                        isNavItemActive(location, item.href)
                          ? 'bg-primary/10 text-primary'
                          : 'text-foreground/70 hover:bg-accent hover:text-foreground'
                      )}
                    >
                      <item.icon className="h-4 w-4 shrink-0" />
                      <span>{item.label}</span>
                    </div>
                  </Link>
                ))}

                {/* Role-specific items */}
                {showModelReviewerItems && (
                  <>
                    <div className="my-2 h-px w-full bg-border" />
                    {MODEL_REVIEWER_ITEMS.map((item) => (
                      <Link key={item.href} href={item.href} onClick={() => setMobileMenuOpen(false)}>
                        <div
                          className={cn(
                            'flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 font-medium text-xs transition-all duration-200',
                            isNavItemActive(location, item.href)
                              ? 'bg-primary/10 text-primary'
                              : 'text-foreground/60 hover:bg-accent hover:text-foreground'
                          )}
                        >
                          <item.icon className="h-3.5 w-3.5 shrink-0" />
                          <span>{item.label}</span>
                        </div>
                      </Link>
                    ))}
                  </>
                )}

                {showQaEngineerItems && (
                  <>
                    <div className="my-2 h-px w-full bg-border" />
                    {QA_ENGINEER_ITEMS.map((item) => (
                      <Link key={item.href} href={item.href} onClick={() => setMobileMenuOpen(false)}>
                        <div
                          className={cn(
                            'flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 font-medium text-xs transition-all duration-200',
                            isNavItemActive(location, item.href)
                              ? 'bg-primary/10 text-primary'
                              : 'text-foreground/60 hover:bg-accent hover:text-foreground'
                          )}
                        >
                          <item.icon className="h-3.5 w-3.5 shrink-0" />
                          <span>{item.label}</span>
                        </div>
                      </Link>
                    ))}
                  </>
                )}

                {showClinicAdminItems && (
                  <>
                    <div className="my-2 h-px w-full bg-border" />
                    {CLINIC_ADMIN_ITEMS.map((item) => (
                      <Link key={item.href} href={item.href} onClick={() => setMobileMenuOpen(false)}>
                        <div
                          className={cn(
                            'flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 font-medium text-xs transition-all duration-200',
                            isNavItemActive(location, item.href)
                              ? 'bg-primary/10 text-primary'
                              : 'text-foreground/60 hover:bg-accent hover:text-foreground'
                          )}
                        >
                          <item.icon className="h-3.5 w-3.5 shrink-0" />
                          <span>{item.label}</span>
                        </div>
                      </Link>
                    ))}
                    {showSystemAdminItems &&
                      SYSTEM_ADMIN_ITEMS.map((item) => (
                        <Link key={item.href} href={item.href} onClick={() => setMobileMenuOpen(false)}>
                          <div
                            className={cn(
                              'flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 font-medium text-xs transition-all duration-200',
                              isNavItemActive(location, item.href)
                                ? 'bg-primary/10 text-primary'
                                : 'text-foreground/60 hover:bg-accent hover:text-foreground'
                            )}
                          >
                            <item.icon className="h-3.5 w-3.5 shrink-0" />
                            <span>{item.label}</span>
                          </div>
                        </Link>
                      ))}
                  </>
                )}
              </nav>

              <div className="px-4 pb-2">
                <HelpAndFeedback onNavigate={() => setMobileMenuOpen(false)} />
              </div>

              {/* User info & logout */}
              <div className="border-t p-4">
                <div className="flex w-full items-center gap-3 rounded-md bg-accent/50 p-3">
                  <UserInitials user={user} />
                  <div className="flex-1 overflow-hidden">
                    <p className="truncate font-medium text-sm">{user?.name || 'Notandi'}</p>
                    {currentOrgName && (
                      <p className="truncate text-[10px] text-muted-foreground/70">{currentOrgName}</p>
                    )}
                  </div>
                  <ClinicSwitcher />
                  <LogoutButton
                    onLogout={() => {
                      logout();
                      setMobileMenuOpen(false);
                    }}
                  />
                </div>
              </div>
            </div>
          </SheetContent>
        </Sheet>

        {title && <h1 className="hidden font-semibold text-foreground text-lg md:block">{title}</h1>}
        {isViewingAsDelegate && viewingAsAccount && (
          <div className="hidden items-center gap-1.5 rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-blue-700 text-xs md:flex">
            <Eye className="h-3 w-3" />
            <span>
              Hliðaraðgangur fyrir{' '}
              <span className="font-medium">{formatDelegatorLabel(viewingAsAccount.delegator_account_id)}</span>
            </span>
          </div>
        )}
      </div>

      {/* Center content - absolutely centered on mobile only */}
      {centerContent && <div className="-translate-x-1/2 absolute left-1/2 md:hidden">{centerContent}</div>}

      <div className="flex items-center gap-2 md:gap-4">
        {/* Center content rendered in right section on desktop */}
        {centerContent && <div className="hidden md:flex">{centerContent}</div>}
        {children}
      </div>
    </header>
  );
}
